'use client'
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import CustomFetch from "@/Utils/APIRequest/AxiosInstance";
import { BaseURL, WithoutAuthUserDetailAPI } from "@/Utils/APIRequest/Endpoints";
import { useTranslation } from "@/Utils/Hooks/useTranslate";
import { useLoading } from "@/Utils/Hooks/useLoading";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import InfluencerDetailSkeleton from "@/Component/Common/InfluencerDetail/InfluencerDetailSkeleton";
import DetailHeader from "@/Component/Common/InfluencerDetail/DetailHeader";
import DetailTabs from "@/Component/Common/InfluencerDetail/DetailTabs";
import ImageWithFallback from "@/Component/Common/ImageWithFallback";
import DetailInstagramPosts from "@/Component/Common/InfluencerDetail/SocailMediaPosts/DetailInstagramPosts";
import logo from '@/assets/images/logo.png'
import SkeletonLoader from "@/Layout/SkeletonLoader";
import { CustomToast } from "@/Utils/CustomToast";
import PrimaryButton from "@/Component/Common/Forms/PrimaryButton";
import Cookies from "js-cookie";

const WithoutAuthInfluencer = (props) => {
    const { slug } = props;
    const { translations } = useTranslation();
    const pathname = usePathname();
    const router = useRouter();
    const searchQuery = useSearchParams()
    const campaignIdQuery = searchQuery.get("id")
    const [postId, setPostId] = useState('');
    const [socialMediaType, setSocialMediaType] = useState('');
    const { skeletonLoading, startSkeletonLoading, stopSkeletonLoading } = useLoading();

    const toSegment = (value: string) =>
        (value || "")
            .normalize("NFC")
            .trim()
            .replaceAll("/", "∕");

    const [UserDetailData, setUserDetailData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        setIsAuthenticated(
            !!(Cookies.get('aat') || Cookies.get('bat') || Cookies.get('iat') || Cookies.get('verifyUAT'))
        );
    }, []);

    const blurClass = isAuthenticated
        ? ''
        : "before:content-[''] before:absolute before:left-0 before:right-0 before:bottom-0 before:w-full before:h-1/2 before:bg-white/50 before:backdrop-blur-sm";

    // Get API
    // const { data: UserDetailData, fetchStatus, isPending } = useQuery({ queryKey: [WithoutAuthUserDetailAPI, slug, campaignIdQuery], queryFn: () => CustomFetch({ url: `${WithoutAuthUserDetailAPI}/${slug}`, params: { campaign_id: campaignIdQuery } }, null, false), refetchOnWindowFocus: false, select: (data) => data?.data });


    const fetchUserDetail = async () => {
        try {
            const response = await axios.get(`${BaseURL}${WithoutAuthUserDetailAPI}/${slug}`);

            setUserDetailData(response?.data);

            const expectedRaw = toSegment(response?.data?.data?.display_name || "");
            if (expectedRaw) {
                const parts = pathname.split("/").filter(Boolean);
                const currentRaw = parts.length > 2 ? parts.slice(2).join("/") : "";

                if (currentRaw !== expectedRaw) {
                    const target = `/influencers/${decodeURIComponent(slug)}/${expectedRaw}`;

                    if (window.location.pathname + window.location.search !== target) {
                        window.history.replaceState(null, "", target);
                    }
                }
            }
        } catch (error: any) {

            if (error?.response?.status === 404) {
                // Redirect to 404 page
                router.replace("/not-found");
                return;
            } else {
                CustomToast(
                    "error",
                    error?.response?.data?.message || "Something went wrong, please try again later"
                );
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUserDetail();
    }, [slug, campaignIdQuery]);

    useEffect(() => {
        startSkeletonLoading();
    }, []);

    useEffect(() => {
        if (UserDetailData?.is_blurred) {
            CustomToast('error', "Your limit has been reached please try after sometime")
        }
    }, [UserDetailData?.is_blurred]);

    const dangerousTags = [
        'script',
        'style',
        'iframe',
        'embed',
        'object',
        'img',
        'video',
        'audio',
        'source',
        'link'
    ];

    const sanitizeHtml = (html) => {
        if (!html) return '';
        let result = html;
        dangerousTags.forEach(tag => {
            result = result.replace(new RegExp(`<${tag}[\\s\\S]*?>[\\s\\S]*?<\\/${tag}>`, 'gi'), '');
            result = result.replace(new RegExp(`<${tag}[^>]*?\\/?>`, 'gi'), '');
        });
        return result;
    };

    const formatHtml = (text) => {
        let sanitized = sanitizeHtml(text);
        // Convert URLs to clickable anchor tags
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const formattedText = sanitized?.replace(urlRegex, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">${url}</a>`)?.replace(/\n/g, "<br/>"); // Preserve line breaks

        return formattedText;
    };

    return (
        <div className="h-screen overflow-y-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray/10 [&::-webkit-scrollbar-thumb]:bg-gray/50 relative sm:p-6 p-4">
            <div className="mx-auto max-w-6xl">
                {/* {fetchStatus === "fetching" || isPending ? */}
                {loading ?
                    <>
                        <div className="mb-5">
                            <SkeletonLoader width="170" height={28} rows={1} />
                        </div>
                        <InfluencerDetailSkeleton />
                    </>
                    :
                    <>
                        <div className="flex items-center justify-between mb-5 gap-3">
                            <Link href="/">
                                <Image src={logo} alt='Logo' className="max-sm:w-32 sm:h-10 w-auto" />
                            </Link>
                            <div className="flex items-center gap-3">
                                {!isAuthenticated ? (
                                    <>
                                        <p className="text-sm sm:text-base text-primary">
                                            Join Connector Club to unlock full creator profiles.
                                        </p>
                                        <PrimaryButton title={"Unlock Profile"} onClick={() => router.push('/login')} customClass="sm:mt-0 mt-2" />
                                    </>
                                ) : (
                                    <PrimaryButton title={"Discover Creators"} onClick={() => router.push('/brand/browse-influencer')} customClass="sm:mt-0 mt-2" />
                                )}
                            </div>
                        </div>
                        <div className="border border-lightGray rounded-2xl overflow-hidden">
                            <DetailHeader UserDetailData={UserDetailData?.data} translations={translations} isStar={false} />
                            <div className="py-10 px-5">
                                <div className="grid grid-cols-12 gap-3">
                                    <div className="md:col-span-8 col-span-12">
                                        <div className="border-b border-lightGray pb-5">

                                            {UserDetailData?.data?.about_me && <> <h3 className="mb-2 text-lg font-bold">{translations?.about}</h3>
                                                <p className={`relative text-sm ${blurClass}`} dangerouslySetInnerHTML={{ __html: UserDetailData?.data?.about_me ? formatHtml(UserDetailData?.data?.about_me) : '-' }}></p>
                                            </>
                                            }
                                            {UserDetailData?.data?.ai_summary && <> <h3 className={`mb-2 text-lg font-bold ${UserDetailData?.data?.about_me ? 'mt-5' : ''}`}>{translations?.aiSummary}</h3>
                                                <p className={`relative text-sm ${blurClass}`} dangerouslySetInnerHTML={{ __html: UserDetailData?.data?.ai_summary ? formatHtml(UserDetailData?.data?.ai_summary) : '-' }}></p>
                                            </>
                                            }

                                            {UserDetailData?.data?.influencerCategories?.length > 0 &&
                                                <>
                                                    <h3 className="mb-2 text-lg font-bold mt-5">{translations?.categoryHeaderTitle}</h3>
                                                    <div className='flex flex-wrap gap-2.5'>
                                                        {UserDetailData?.data?.influencerCategories?.map((category) => (
                                                            category?.category !== null &&
                                                            <span className='block bg-gray/25 px-2.5 rounded-md text-sm' key={category?.id}>{category?.category?.title}</span>
                                                        ))}
                                                    </div>
                                                </>
                                            }
                                            {UserDetailData?.data?.userKeywords?.length > 0 &&
                                                <>
                                                    <h3 className="mb-2 text-lg font-bold mt-5">{translations?.tags}</h3>
                                                    <div className='flex flex-wrap gap-2.5'>
                                                        {UserDetailData?.data?.userKeywords?.map((keyword) => (
                                                            <span className='block bg-gray/25 px-2.5 rounded-md text-sm' key={keyword?.id}>{keyword?.keywords?.keyword}</span>
                                                        ))}
                                                    </div>
                                                </>
                                            }
                                        </div>
                                        <div className="mt-4">
                                            <DetailTabs UserDetailData={UserDetailData?.data} setPostId={setPostId} setSocialMediaType={setSocialMediaType} socialMediaType={socialMediaType} translations={translations} />
                                        </div>
                                    </div>
                                    <div className="md:col-span-4 col-span-12">
                                        <div className="md:border md:border-lightGray md:p-5 rounded-2xl min-h-[250px] xl:min-h-[350px] max-md:mt-5">
                                            <h3 className="text-lg font-bold mb-7">{translations?.topPost}</h3>
                                            {(UserDetailData?.data?.socialMediaPosts?.length > 0) ? (
                                                <div className='grid grid-cols-3 gap-2.5'>
                                                    {UserDetailData?.data?.socialMediaPosts?.map((post, index) => (
                                                        <div className='w-full overflow-hidden aspect-square bg-gray/20 relative cursor-pointer' key={index} onClick={() => { window.open(post?.post_url, "_blank") }}>
                                                            <ImageWithFallback src={post?.image_url ?? post?.thumbnails ?? post?.thumbnail_url} alt="Post" fill className='w-full h-full object-cover' />
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="bg-gray/20 w-full h-full flex items-center justify-center p-5 rounded-xl text-xl font-semibold text-gray">{translations?.noPostFound}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {!UserDetailData?.is_blurred && <h3 className="text-lg font-bold mt-8 mb-7">{translations?.allPosts}</h3>}
                                {socialMediaType === 'instagram' && <DetailInstagramPosts mediaData={UserDetailData?.data?.social_medias?.find(media => media.social_type === 'instagram')} postId={postId} setPostId={setPostId} skeletonLoading={skeletonLoading} stopSkeletonLoading={stopSkeletonLoading} params={{ social_type: 'instagram', type: 'recent', url: "permalink", likeKey: "like_count", imageKey: "thumbnail_url", created_at: "instagram_timestamp" }} key={socialMediaType} translations={translations} socialMediaType={socialMediaType} />}

                                {socialMediaType === 'facebook' && <DetailInstagramPosts mediaData={UserDetailData?.data?.social_medias?.find(media => media.social_type === 'facebook')} postId={postId} setPostId={setPostId} skeletonLoading={skeletonLoading} stopSkeletonLoading={stopSkeletonLoading} params={{ social_type: 'facebook', type: 'recent', url: "post_url", likeKey: "like_counts", imageKey: "thumbnails", created_at: "created_time" }} key={socialMediaType} translations={translations} socialMediaType={socialMediaType} />}

                                {socialMediaType === 'youtube' && <DetailInstagramPosts mediaData={UserDetailData?.data?.social_medias?.find(media => media.social_type === 'youtube')} postId={postId} setPostId={setPostId} skeletonLoading={skeletonLoading} stopSkeletonLoading={stopSkeletonLoading} params={{ social_type: 'youtube', type: 'recent', url: "post_url", likeKey: "view_counts", imageKey: "thumbnails", created_at: "published_at" }} key={socialMediaType} translations={translations} socialMediaType={socialMediaType} />}

                                {socialMediaType === 'tiktok' && <DetailInstagramPosts mediaData={UserDetailData?.data?.social_medias?.find(media => media.social_type === 'tiktok')} postId={postId} setPostId={setPostId} skeletonLoading={skeletonLoading} stopSkeletonLoading={stopSkeletonLoading} params={{ social_type: 'tiktok', type: 'recent', url: "share_url", likeKey: "view_count", imageKey: "cover_image_url", created_at: "create_time" }} key={socialMediaType} translations={translations} socialMediaType={socialMediaType} />}

                            </div>
                        </div>
                    </>
                }
            </div>
        </div>
    )
}

export default WithoutAuthInfluencer
